import React, { useCallback, useEffect, useRef, useState } from 'react'
import axios from 'axios'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import {
  SESSION_TIMEOUT_EVENT,
  clearSessionExpiredFlag,
  isSessionTimeoutError,
} from '../../utils/sessionTimeout'

const LAST_ACTIVITY_KEY = 'rs_last_activity'
const TIMEOUT_MINUTES_KEY = 'rs_session_timeout_minutes'
const ACTIVITY_EVENTS = ['mousedown', 'keydown', 'scroll', 'touchstart', 'click']
const DEFAULT_TIMEOUT_MINUTES = 120
const HEARTBEAT_INTERVAL_MS = 4 * 60 * 1000
const IDLE_VERIFY_RETRY_MS = 60 * 1000

function readLastActivity() {
  const raw = sessionStorage.getItem(LAST_ACTIVITY_KEY)
  const value = Number(raw)
  return Number.isFinite(value) ? value : Date.now()
}

function writeLastActivity(at = Date.now()) {
  sessionStorage.setItem(LAST_ACTIVITY_KEY, String(at))
}

function clearLastActivity() {
  sessionStorage.removeItem(LAST_ACTIVITY_KEY)
}

export default function SessionTimeoutWatcher() {
  const { user, logout } = useAuth()
  const navigate = useNavigate()
  const [expired, setExpired] = useState(false)
  const [timeoutMinutes, setTimeoutMinutes] = useState(() => {
    const stored = Number(sessionStorage.getItem(TIMEOUT_MINUTES_KEY))
    return Number.isFinite(stored) && stored >= 5 ? stored : DEFAULT_TIMEOUT_MINUTES
  })
  const idleTimerRef = useRef(null)
  const heartbeatRef = useRef(null)
  const expiredRef = useRef(false)
  const throttleRef = useRef(0)
  const timeoutMinutesRef = useRef(timeoutMinutes)

  useEffect(() => {
    timeoutMinutesRef.current = timeoutMinutes
  }, [timeoutMinutes])

  const clearIdleTimer = useCallback(() => {
    if (idleTimerRef.current) {
      window.clearTimeout(idleTimerRef.current)
      idleTimerRef.current = null
    }
  }, [])

  const clearHeartbeat = useCallback(() => {
    if (heartbeatRef.current) {
      window.clearInterval(heartbeatRef.current)
      heartbeatRef.current = null
    }
  }, [])

  const markSessionExpired = useCallback(() => {
    if (expiredRef.current) return
    expiredRef.current = true
    sessionStorage.setItem('rs_session_expired', '1')
    clearIdleTimer()
    clearHeartbeat()
    setExpired(true)
  }, [clearIdleTimer, clearHeartbeat])

  const pingServerSession = useCallback(async ({ bumpClient = true } = {}) => {
    const res = await axios.get('/api/auth/heartbeat')
    const minutes = Math.max(5, Number(res.data?.sessionTimeoutMinutes) || DEFAULT_TIMEOUT_MINUTES)
    sessionStorage.setItem(TIMEOUT_MINUTES_KEY, String(minutes))
    setTimeoutMinutes(minutes)
    if (bumpClient) writeLastActivity(Date.now())
    return minutes
  }, [])

  const onIdleDeadlineRef = useRef(null)

  const rescheduleIdleTimer = useCallback(() => {
    clearIdleTimer()
    const lastActivity = readLastActivity()
    const timeoutMs = timeoutMinutesRef.current * 60 * 1000
    const remaining = timeoutMs - (Date.now() - lastActivity)
    if (remaining <= 0) {
      onIdleDeadlineRef.current?.()
      return
    }
    idleTimerRef.current = window.setTimeout(() => {
      onIdleDeadlineRef.current?.()
    }, remaining)
  }, [clearIdleTimer])

  const onIdleDeadline = useCallback(async () => {
    if (expiredRef.current || !localStorage.getItem('token')) return
    try {
      await pingServerSession()
      rescheduleIdleTimer()
    } catch (err) {
      if (isSessionTimeoutError(err)) {
        markSessionExpired()
        return
      }
      if (!err?.response) {
        idleTimerRef.current = window.setTimeout(() => {
          onIdleDeadlineRef.current?.()
        }, IDLE_VERIFY_RETRY_MS)
        return
      }
      markSessionExpired()
    }
  }, [markSessionExpired, pingServerSession, rescheduleIdleTimer])

  useEffect(() => {
    onIdleDeadlineRef.current = onIdleDeadline
  }, [onIdleDeadline])

  const bumpActivity = useCallback(() => {
    if (!user || expiredRef.current) return
    const now = Date.now()
    if (now - throttleRef.current < 1000) return
    throttleRef.current = now
    writeLastActivity(now)
    rescheduleIdleTimer()
  }, [user, rescheduleIdleTimer])

  const handleSignIn = () => {
    clearIdleTimer()
    clearHeartbeat()
    clearLastActivity()
    clearSessionExpiredFlag()
    expiredRef.current = false
    setExpired(false)
    logout()
    navigate('/login', { replace: true })
  }

  const hasToken = typeof window !== 'undefined' && !!localStorage.getItem('token')

  useEffect(() => {
    if (!user) {
      clearIdleTimer()
      clearHeartbeat()
      if (!hasToken) {
        expiredRef.current = false
        setExpired(false)
      }
      return undefined
    }

    let cancelled = false
    expiredRef.current = false
    setExpired(false)
    clearSessionExpiredFlag()
    writeLastActivity()

    pingServerSession()
      .then(() => {
        if (cancelled) return
        rescheduleIdleTimer()
      })
      .catch(() => {
        if (cancelled) return
        rescheduleIdleTimer()
      })

    heartbeatRef.current = window.setInterval(() => {
      if (document.hidden || expiredRef.current) return
      pingServerSession({ bumpClient: false })
        .catch((err) => {
          if (isSessionTimeoutError(err)) markSessionExpired()
        })
    }, HEARTBEAT_INTERVAL_MS)

    const onActivity = () => bumpActivity()
    ACTIVITY_EVENTS.forEach((eventName) => {
      window.addEventListener(eventName, onActivity, { passive: true })
    })

    const onVisibility = () => {
      if (document.hidden || !user) return
      bumpActivity()
      pingServerSession()
        .then(() => rescheduleIdleTimer())
        .catch(() => {})
    }
    document.addEventListener('visibilitychange', onVisibility)
    window.addEventListener('focus', onVisibility)

    const onSessionTimeout = () => markSessionExpired()
    window.addEventListener(SESSION_TIMEOUT_EVENT, onSessionTimeout)

    return () => {
      cancelled = true
      clearIdleTimer()
      clearHeartbeat()
      ACTIVITY_EVENTS.forEach((eventName) => {
        window.removeEventListener(eventName, onActivity)
      })
      document.removeEventListener('visibilitychange', onVisibility)
      window.removeEventListener('focus', onVisibility)
      window.removeEventListener(SESSION_TIMEOUT_EVENT, onSessionTimeout)
    }
  }, [
    user,
    hasToken,
    bumpActivity,
    clearIdleTimer,
    clearHeartbeat,
    markSessionExpired,
    pingServerSession,
    rescheduleIdleTimer,
  ])

  useEffect(() => {
    if (!user || expiredRef.current) return undefined
    rescheduleIdleTimer()
  }, [timeoutMinutes, user, rescheduleIdleTimer])

  if (!expired || (!user && !hasToken)) return null

  return (
    <div className="session-timeout-overlay" role="presentation">
      <div
        className="session-timeout-modal"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="session-timeout-title"
        aria-describedby="session-timeout-message"
      >
        <div className="session-timeout-icon" aria-hidden="true">⏱</div>
        <h2 id="session-timeout-title" className="session-timeout-title">Session Timed Out</h2>
        <p id="session-timeout-message" className="session-timeout-message">
          You have been signed out after {timeoutMinutes} minutes of inactivity. Please sign in again to continue.
        </p>
        <button type="button" className="session-timeout-btn" onClick={handleSignIn}>
          Sign In
        </button>
      </div>
    </div>
  )
}
